import React, { useState, useEffect, useRef } from "react";
import "./EditArticle.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useContributorAuth } from "../../context/ContributorAuthContext";

const SERVER_URL = process.env.REACT_APP_BASE_URL;

const PhotoUpload = ({ photo, onPhotoChange }) => {
  const fileInputRef = useRef(null);

  const handleClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="flex items-center justify-center w-full">
      <label
        htmlFor="photo-upload"
        className="flex flex-col items-center justify-center w-24 h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        {photo ? (
          <img
            src={photo}
            alt="Uploaded"
            className="w-full h-full object-cover rounded-lg"
          />
        ) : (
          <div className="flex flex-col items-center justify-center">
            <i className="fas fa-camera text-gray-500 text-lg mb-1"></i>
            <span className="text-xs text-gray-500">Upload</span>
          </div>
        )}
        <input
          ref={fileInputRef}
          id="photo-upload"
          type="file"
          accept="image/*"
          onChange={(e) => {
            if (e.target.files[0]) {
              onPhotoChange(e.target.files[0]);
            }
          }}
          className="hidden"
        />
      </label>
    </div>
  );
};

const EditArticle = () => {
  const [article, setArticle] = useState({
    sportsCategory: "",
    mainHeading: "",
    mainDescription: "",
    mainPhotos: [],
    subHeadings: [],
    references: [],
  });

  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const { contributor, loading: authLoading } = useContributorAuth();

  useEffect(() => {
    if (!authLoading && !contributor) {
      navigate("/loginContributor", { replace: true });
      return;
    }
    const fetchArticle = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/article/get-article/${id}`
        );
        if (response.ok) {
          const data = await response.json();
          // console.log("Fetched data:", data); // Log the raw data

          const decodeBuffer = (buffer) => {
            if (!buffer || !buffer.data || buffer.data.length === 0)
              return null;
            const decoded = buffer.data
              .map((byte) => String.fromCharCode(byte))
              .join("");
            return decoded === "null" ? null : decoded;
          };

          setArticle({
            sportsCategory: data.sport_name || "",
            mainHeading: data.mainHeading?.heading || "",
            mainDescription: data.mainHeading?.description || "",
            mainPhotos: data.pictures
              .filter((pic) => pic.main_heading_id !== null)
              .map((pic) => ({
                photo: decodeBuffer(pic.image),
                description: "", // Add description if available in your data
              })),
            subHeadings: data.subHeadings.map((sh) => ({
              heading: sh.sub_heading,
              description: sh.description,
              photos: data.pictures
                .filter((pic) => pic.sub_heading_id === sh.id)
                .map((pic) => ({
                  photo: decodeBuffer(pic.image),
                  description: "",
                })),
            })),
            references: data.reference
              ? data.reference.map((ref) => ref.content || "")
              : [],
          });
          setLoading(false);
        } else {
          console.error("Failed to fetch article");
        }
      } catch (error) {
        console.error("Error:", error);
      }
    };

    if (!authLoading && contributor) {
      fetchArticle();
    }
  }, [id, contributor, authLoading]);

  // New useEffect to log the updated article state
  useEffect(() => {
    if (!loading) {
      // console.log("Updated article state:", article);
      if (article.mainPhotos.length > 0) {
        // console.log("First main photo:", article.mainPhotos[0].photo);
      }
    }
  }, [article, loading]);

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////

  const handleMainDescriptionChange = (e) => {
    setArticle({ ...article, mainDescription: e.target.value });
  };

  const handleMainPhotoChange = (index, field, value) => {
    const updatedPhotos = article.mainPhotos.map((photo, i) =>
      i === index ? { ...photo, [field]: value } : photo
    );
    setArticle({ ...article, mainPhotos: updatedPhotos });
  };

  const handleMainPhotoAdd = () => {
    setArticle({
      ...article,
      mainPhotos: [...article.mainPhotos, { photo: null, description: "" }],
    });
  };

  const handleSubHeadingChange = (index, field, value) => {
    const updatedSubHeadings = article.subHeadings.map((subHeading, i) =>
      i === index ? { ...subHeading, [field]: value } : subHeading
    );
    setArticle({ ...article, subHeadings: updatedSubHeadings });
  };

  const handleSubHeadingAdd = () => {
    setArticle({
      ...article,
      subHeadings: [
        ...article.subHeadings,
        { heading: "", description: "", photos: [] },
      ],
    });
  };

  const handleSubHeadingPhotoAdd = (subHeadingIndex) => {
    const updatedSubHeadings = article.subHeadings.map((subHeading, index) =>
      index === subHeadingIndex
        ? {
            ...subHeading,
            photos: [...subHeading.photos, { photo: null, description: "" }],
          }
        : subHeading
    );
    setArticle({ ...article, subHeadings: updatedSubHeadings });
  };

  const handleSubHeadingPhotoChange = (
    subHeadingIndex,
    photoIndex,
    field,
    value
  ) => {
    const updatedSubHeadings = article.subHeadings.map((subHeading, i) =>
      i === subHeadingIndex
        ? {
            ...subHeading,
            photos: subHeading.photos.map((photo, j) =>
              j === photoIndex ? { ...photo, [field]: value } : photo
            ),
          }
        : subHeading
    );
    setArticle({ ...article, subHeadings: updatedSubHeadings });
  };

  const handleReferenceChange = (index, value) => {
    const updatedReferences = article.references.map((ref, i) =>
      i === index ? value : ref
    );
    setArticle({ ...article, references: updatedReferences });
  };

  const handleReferenceAdd = () => {
    setArticle({ ...article, references: [...article.references, ""] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!article.mainHeading.trim()) {
      toast.error("Main heading is required");
      return;
    }
    if (!article.mainDescription.trim()) {
      toast.error("Description is required");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("sportsCategory", article.sportsCategory);
      formData.append("mainHeading", article.mainHeading);
      formData.append("mainDescription", article.mainDescription);
      formData.append("subHeadings", JSON.stringify(article.subHeadings));
      formData.append("references", JSON.stringify(article.references));

      // Append main photos
      article.mainPhotos.forEach((photo, index) => {
        if (photo.photo instanceof File) {
          formData.append(`photos`, photo.photo);
        }
      });

      // Append subheading photos
      article.subHeadings.forEach((subHeading, subIndex) => {
        subHeading.photos.forEach((photo, photoIndex) => {
          if (photo.photo instanceof File) {
            formData.append(`photos`, photo.photo);
          }
        });
      });

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/article/update-article/${id}`,
        {
          method: "PUT",
          body: formData,
          credentials: "include",
        }
      );
      if (response.ok) {
        toast.success(
          "Article updated successfully! Waiting for admin approval."
        );
        navigate("/contributor-dashboard");
      } else {
        toast.error("Failed to update article. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        "An error occurred while updating the article. Please try again."
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0f2f5] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-[#3498db] bg-white transition ease-in-out duration-150 cursor-not-allowed">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#3498db]"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f2f5] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <img
            src="/logo.png"
            alt="Logo"
            className="h-[50px] sm:h-[60px] mx-auto mb-4 sm:mb-6 cursor-pointer hover:opacity-80 transition-opacity drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
            onClick={() => navigate(-1)}
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-[#3498db] mb-2">
            Edit Article
          </h1>
          <p className="text-[#57606f] mt-2 text-sm sm:text-base">
            Update and modify your sports article
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.05)] rounded-2xl p-4 sm:p-8 space-y-6 sm:space-y-8 backdrop-blur-[10px] border border-white/20"
        >
          {/* Article Information */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Article Information
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sports Category
                </label>
                <div className="w-full h-11 px-3 py-2 rounded-lg text-base border border-[#ddd] bg-[#f8f9fa] text-gray-700">
                  {article.sportsCategory}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main Heading <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={article.mainHeading}
                  onChange={(e) =>
                    setArticle({ ...article, mainHeading: e.target.value })
                  }
                  placeholder="Enter main heading"
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="mainDescription"
                  value={article.mainDescription}
                  onChange={handleMainDescriptionChange}
                  placeholder="Enter article description"
                  rows="4"
                  className="w-full px-3 py-2 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
              </div>
            </div>
          </div>

          {/* Main Photos */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Main Photos{" "}
              <span className="text-gray-500 text-sm font-normal">
                (Optional)
              </span>
            </h2>

            <div className="space-y-4">
              {article.mainPhotos.map((photo, index) => (
                <div
                  key={index}
                  className="border border-[#ddd] rounded-lg p-4"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <PhotoUpload
                      photo={
                        photo.photo
                          ? `${SERVER_URL}/${photo.photo.replace(/\\/g, "/")}`
                          : null
                      }
                      onPhotoChange={(file) =>
                        handleMainPhotoChange(index, "photo", file)
                      }
                    />
                    {article.mainPhotos.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updatedPhotos = article.mainPhotos.filter(
                            (_, i) => i !== index
                          );
                          setArticle({ ...article, mainPhotos: updatedPhotos });
                        }}
                        className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Photo description"
                    value={photo.description || ""}
                    onChange={(e) =>
                      handleMainPhotoChange(
                        index,
                        "description",
                        e.target.value
                      )
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={handleMainPhotoAdd}
                className="w-full h-11 px-4 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
              >
                + Add More Photos
              </button>
            </div>
          </div>

          {/* Sub-Headings */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Sub-Headings{" "}
              <span className="text-gray-500 text-sm font-normal">
                (Optional)
              </span>
            </h2>

            <div className="space-y-4 sm:space-y-6">
              {article.subHeadings.map((subHeading, subIndex) => (
                <div
                  key={subIndex}
                  className="border border-[#ddd] rounded-lg p-4 sm:p-6 space-y-4"
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="text"
                      placeholder="Sub-Heading"
                      value={subHeading.heading}
                      onChange={(e) =>
                        handleSubHeadingChange(
                          subIndex,
                          "heading",
                          e.target.value
                        )
                      }
                      className="flex-1 h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    />
                    {article.subHeadings.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updatedSubHeadings = article.subHeadings.filter(
                            (_, i) => i !== subIndex
                          );
                          setArticle({
                            ...article,
                            subHeadings: updatedSubHeadings,
                          });
                        }}
                        className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <textarea
                    placeholder="Description"
                    value={subHeading.description}
                    onChange={(e) =>
                      handleSubHeadingChange(
                        subIndex,
                        "description",
                        e.target.value
                      )
                    }
                    rows="4"
                    className="w-full px-3 py-2 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sub-Heading Photos{" "}
                      <span className="text-gray-500">(Optional)</span>
                    </label>
                    <div className="space-y-4">
                      {subHeading.photos.map((photo, photoIndex) => (
                        <div
                          key={photoIndex}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-center gap-4 mb-4">
                            <PhotoUpload
                              photo={
                                photo.photo
                                  ? `${SERVER_URL}/${photo.photo.replace(
                                      /\\/g,
                                      "/"
                                    )}`
                                  : null
                              }
                              onPhotoChange={(file) =>
                                handleSubHeadingPhotoChange(
                                  subIndex,
                                  photoIndex,
                                  "photo",
                                  file
                                )
                              }
                            />
                            {subHeading.photos.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedSubHeadings =
                                    article.subHeadings.map((sub, i) =>
                                      i === subIndex
                                        ? {
                                            ...sub,
                                            photos: sub.photos.filter(
                                              (_, j) => j !== photoIndex
                                            ),
                                          }
                                        : sub
                                    );
                                  setArticle({
                                    ...article,
                                    subHeadings: updatedSubHeadings,
                                  });
                                }}
                                className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            placeholder="Photo description"
                            value={photo.description || ""}
                            onChange={(e) =>
                              handleSubHeadingPhotoChange(
                                subIndex,
                                photoIndex,
                                "description",
                                e.target.value
                              )
                            }
                            className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleSubHeadingPhotoAdd(subIndex)}
                        className="w-full h-11 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-300 font-semibold hover:scale-105 active:scale-100"
                      >
                        + Add More Photos
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={handleSubHeadingAdd}
                className="w-full h-11 px-4 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
              >
                + Add New Sub-Heading
              </button>
            </div>
          </div>

          {/* References */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              References{" "}
              <span className="text-gray-500 text-sm font-normal">
                (Optional)
              </span>
            </h2>

            <div className="space-y-4">
              {article.references.map((reference, index) => (
                <div key={index} className="flex items-center gap-4">
                  <input
                    type="text"
                    placeholder="Reference"
                    value={reference}
                    onChange={(e) =>
                      handleReferenceChange(index, e.target.value)
                    }
                    className="flex-1 h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  {article.references.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const updatedReferences = article.references.filter(
                          (_, i) => i !== index
                        );
                        setArticle({
                          ...article,
                          references: updatedReferences,
                        });
                      }}
                      className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={handleReferenceAdd}
                className="w-full h-11 px-4 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
              >
                + Add New Reference
              </button>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="pt-4 sm:pt-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="submit"
                className="flex-1 h-11 flex justify-center items-center border border-transparent rounded-lg text-sm sm:text-base font-semibold text-white bg-[#3498db] hover:bg-[#2980b9] focus:outline-none transition-all duration-300 uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
              >
                Update Article
              </button>
              <button
                type="button"
                className="flex-1 h-11 bg-white text-[#3498db] border border-[#3498db] rounded-lg hover:bg-[#f0f8ff] transition-all duration-300 font-semibold hover:scale-105 active:scale-100 text-sm sm:text-base"
                onClick={() => navigate("/contributor-dashboard")}
              >
                Back
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditArticle;
